// ============================================
// 4. MAIN FORM (ClientForm.jsx) - WITH PACKAGE NAME & ALPHABETICAL SERVICES
// ============================================
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "intl-tel-input/build/css/intlTelInput.css";
import intlTelInput from "intl-tel-input";
import CurrenciesWidget, { getCurrencySymbol } from "../widgets/CurrenciesWidget";
import NationalitiesWidget from "../widgets/NationalitiesWidget";
import ServicesWidget from "../widgets/ServicesWidget";

export default function ClientForm() {
    const [companyQuery, setCompanyQuery] = useState("");
    const [companySuggestions, setCompanySuggestions] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [contactVisible, setContactVisible] = useState(true);
    const [loadingRate, setLoadingRate] = useState(false);
    const [rateLastUpdated, setRateLastUpdated] = useState(null);

    const [contactQuery, setContactQuery] = useState("");
    const [contactSuggestions, setContactSuggestions] = useState([]);
    const [selectedContactId, setSelectedContactId] = useState(null);

    // Simple focus tracking
    const [companyFocused, setCompanyFocused] = useState(false);
    const [contactFocused, setContactFocused] = useState(false);

    // Package name options
    const packageOptions = [
        "Coast Package",
        "Safari Package",
        "Hotel Package",
        "Standard Package",
        "Premium Package",
        "Deluxe Package",
        "Executive Package",
        "Custom Package",
        "Group Package",
        "Corporate Package",
        "Family Package",
        "Adventure Package",
        "Luxury Package",
        "Economy Package",
        "Business Package"
    ];

    const [formData, setFormData] = useState({
        contact_name: "",
        nationality: "",
        email: "",
        phone_number: "",
        nature_of_service: [],
        package_name: "",
        destination: "",
        currency: "KES",
        quotation_amount: "",
        booking_date: "",
        date_from: "",
        date_to: "",
        conversion_status: "",
        exchange_rate: "",
        base_currency: "USD",
    });

    const phoneInputRef = useRef(null);
    const token = localStorage.getItem("token");
    let phoneInstance = null;

    // Initialize intl-tel-input
    useEffect(() => {
        if (phoneInputRef.current) {
            phoneInstance = intlTelInput(phoneInputRef.current, {
                initialCountry: "auto",
                geoIpLookup: function (callback) {
                    fetch("https://ipapi.co/json")
                        .then(res => res.json())
                        .then(data => callback(data.country_code))
                        .catch(() => callback("us"));
                },
                placeholderNumberType: "MOBILE",
                separateDialCode: true,
                preferredCountries: ['ke', 'us', 'gb', 'ng', 'za', 'in', 'au', 'ca'],
            });
        }
        return () => {
            if (phoneInstance) {
                phoneInstance.destroy();
            }
        };
    }, []);

    // Fetch exchange rate
    useEffect(() => {
        if (formData.currency && formData.base_currency) {
            fetchExchangeRate();
        }
    }, [formData.currency, formData.base_currency]);

    // Fetch company suggestions - only when focused
    useEffect(() => {
        const fetchCompanies = async () => {
            if (!companyFocused || companyQuery.length < 2) {
                setCompanySuggestions([]);
                return;
            }
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/companies/search?q=${companyQuery}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                const data = await res.json();
                setCompanySuggestions(data);
            } catch (err) {
                console.error(err);
            }
        };
        fetchCompanies();
    }, [companyQuery, companyFocused]);

    // Fetch contact suggestions - only when focused
    useEffect(() => {
        const fetchContacts = async () => {
            if (!contactFocused || contactQuery.length < 2) {
                setContactSuggestions([]);
                return;
            }
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/clients?q=${contactQuery}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                const data = await res.json();
                setContactSuggestions(data);
            } catch (err) {
                console.error(err);
            }
        };
        fetchContacts();
    }, [contactQuery, contactFocused]);

    const fetchExchangeRate = async () => {
        if (formData.base_currency === formData.currency) {
            setFormData(prev => ({ ...prev, exchange_rate: "1.0000" }));
            setRateLastUpdated(new Date());
            return;
        }

        setLoadingRate(true);
        try {
            const response = await axios.get(
                `https://api.exchangerate-api.com/v4/latest/${formData.currency}`
            );
            const rate = response.data.rates[formData.base_currency];
            if (rate) {
                setFormData(prev => ({
                    ...prev,
                    exchange_rate: rate.toFixed(4)
                }));
                setRateLastUpdated(new Date());
            }
        } catch (error) {
            console.error("Error fetching exchange rate:", error);
            try {
                const fallbackResponse = await axios.get(
                    `https://api.frankfurter.app/latest?from=${formData.currency}&to=${formData.base_currency}`
                );
                const rate = fallbackResponse.data.rates[formData.base_currency];
                if (rate) {
                    setFormData(prev => ({
                        ...prev,
                        exchange_rate: rate.toFixed(4)
                    }));
                    setRateLastUpdated(new Date());
                }
            } catch (fallbackError) {
                console.error("Fallback API also failed:", fallbackError);
            }
        } finally {
            setLoadingRate(false);
        }
    };

    const handleRefreshRate = () => {
        fetchExchangeRate();
    };

    const handleCompanySelect = (company) => {
        setCompanyQuery(company.company_name);
        setSelectedCompanyId(company.id);
        handleContactSelect(company);
        setCompanySuggestions([]);
        setCompanyFocused(false);
        setContactVisible(false);
    };

    const handleContactSelect = (contact) => {
        // Auto-fill contact details
        setContactQuery(contact.contact_name);
        setSelectedContactId(contact.id);
        setContactSuggestions([]);
        setContactFocused(false);

        // Auto-fill company details from contact
        if (contact.company_name) {
            setCompanyQuery(contact.company_name);
        }
        if (contact.company_id) {
            setSelectedCompanyId(contact.company_id);
            setContactVisible(false);
        }

        // Auto-fill form
        setFormData(prev => ({
            ...prev,
            contact_name: contact.contact_name,
            email: contact.email || prev.email,
            phone_number: contact.phone_number || prev.phone_number,
            nationality: contact.nationality || prev.nationality
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'contact_name') {
            setContactQuery(value);
            // Reset selection if user types manually
            if (selectedContactId) {
                setSelectedContactId(null);
                setContactVisible(true);
            }
        }
    };

    const handleCompanyFocus = () => {
        setCompanyFocused(true);
    };

    const handleCompanyBlur = () => {
        setTimeout(() => {
            setCompanyFocused(false);
        }, 200);
    };

    const handleContactFocus = () => {
        setContactFocused(true);
    };

    const handleContactBlur = () => {
        setTimeout(() => {
            setContactFocused(false);
        }, 200);
    };

    const handleServiceToggle = (service, checked) => {
        setFormData(prev => {
            const newServices = checked
                ? [...prev.nature_of_service, service]
                : prev.nature_of_service.filter(v => v !== service);
            return { ...prev, nature_of_service: newServices };
        });
    };

    const handleCurrencyChange = (currency) => {
        setFormData(prev => ({ ...prev, currency }));
    };

    const handleBaseCurrencyChange = (baseCurrency) => {
        setFormData(prev => ({ ...prev, base_currency: baseCurrency }));
    };

    const handleNationalityChange = (nationality) => {
        setFormData(prev => ({ ...prev, nationality }));
    };

    const handlePackageChange = (e) => {
        setFormData(prev => ({ ...prev, package_name: e.target.value }));
    };

    const calculateConvertedAmount = () => {
        if (!formData.quotation_amount || !formData.exchange_rate) return "";
        const amount = parseFloat(formData.quotation_amount);
        const rate = parseFloat(formData.exchange_rate);
        if (isNaN(amount) || isNaN(rate)) return "";

        if (formData.currency === formData.base_currency) {
            return amount.toFixed(2);
        }
        return (amount * rate).toFixed(2);
    };

    const getConversionDescription = () => {
        if (!formData.quotation_amount || !formData.exchange_rate) return "Enter amount and exchange rate to calculate";
        const amount = parseFloat(formData.quotation_amount);
        const rate = parseFloat(formData.exchange_rate);
        if (isNaN(amount) || isNaN(rate)) return "Enter valid numbers";

        if (formData.currency === formData.base_currency) {
            return `${amount} ${formData.currency} (same currency, no conversion)`;
        }

        const converted = (amount * rate).toFixed(2);
        return `${amount} ${formData.currency} = ${converted} ${formData.base_currency}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            company_name: companyQuery,
            company_id: selectedCompanyId,
            contact_id: selectedContactId,
            ...formData,
        };

        try {
            await axios.post(process.env.REACT_APP_API_URL + "/clients", payload, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            alert("Saved successfully");
            window.location.reload();
        } catch (err) {
            console.error(err);
            alert("Error saving record");
        }
    };

    // Check if more than 1 service is selected
    const showPackageField = formData.nature_of_service.length > 1;

    return (
        <div className="container-fluid my-5">
            <div className="row">
                <div className="col-12">
                    <div className="form-container shadow-sm p-4">
                        <h3 className="text-center mb-4">Client Database Entry</h3>
                        <form onSubmit={handleSubmit}>
                            {/* Company Name */}
                            <div className="mb-3 position-relative">
                                <label>Company Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={companyQuery}
                                    onChange={(e) => setCompanyQuery(e.target.value)}
                                    onFocus={handleCompanyFocus}
                                    onBlur={handleCompanyBlur}
                                    autoComplete="off"
                                    required
                                    placeholder="Type to search for company"
                                />
                                {companyFocused && companySuggestions.length > 0 && (
                                    <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                                        {companySuggestions.map(c => (
                                            <li
                                                key={c.id}
                                                className="list-group-item list-group-item-action"
                                                onClick={() => handleCompanySelect(c)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {c.company_name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <style>
                                {`
                                .tel { width:100% !important; }
                                .list-group-item:hover { background-color: #f8f9fa; }
                                `}
                            </style>

                            {/* Contact Name & Nationality - Same Row */}
                            <div className="row">
                                <div className="mb-3 col-md-6 position-relative">
                                    <label>Names (Point of Contact)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="contact_name"
                                        value={formData.contact_name}
                                        onChange={handleChange}
                                        onFocus={handleContactFocus}
                                        onBlur={handleContactBlur}
                                        placeholder="Type to search for contact"
                                        autoComplete="off"
                                        required
                                    />
                                    {contactFocused && contactSuggestions.length > 0 && (
                                        <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                                            {contactSuggestions.map(c => (
                                                <li
                                                    key={c.id}
                                                    className="list-group-item list-group-item-action"
                                                    onClick={() => handleContactSelect(c)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {c.contact_name}
                                                    {c.company_name && ` (${c.company_name})`}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="mb-3 col-md-6">
                                    <NationalitiesWidget
                                        selectedNationality={formData.nationality}
                                        onNationalityChange={handleNationalityChange}
                                    />
                                </div>
                            </div>

                            {/* Contact Details - Hidden after autofill */}
                            {contactVisible && (
                                <div className="row">
                                    <div className="mb-3 col-md-6">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="example@email.com"
                                        />
                                    </div>
                                    <div className="mb-3 col-md-6">
                                        <label>Phone number</label>
                                        <input
                                            type="tel"
                                            className="form-control tel"
                                            name="phone_number"
                                            ref={phoneInputRef}
                                            value={formData.phone_number}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Services Widget - Alphabetical Order */}
                            <ServicesWidget
                                selectedServices={formData.nature_of_service}
                                onServiceToggle={handleServiceToggle}
                                columns={3}
                                alphabetical={true}
                            />

                            {/* Package Name - Only shows when more than 1 service is selected */}
                            {showPackageField && (
                                <div className="row">
                                    <div className="mb-3 col-md-6">
                                        <label>Package Name</label>
                                        <select
                                            className="form-control"
                                            name="package_name"
                                            value={formData.package_name}
                                            onChange={handlePackageChange}
                                        >
                                            <option value="">Select Package</option>
                                            {packageOptions.map((pkg, index) => (
                                                <option key={index} value={pkg}>
                                                    {pkg}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="text-muted">
                                            Select a package name for multiple services
                                        </small>
                                    </div>
                                </div>
                            )}

                            {/* Destination / Quote */}
                            <div className="row">
                                <div className="mb-3 col-md-4">
                                    <label>Destination</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="destination"
                                        value={formData.destination}
                                        onChange={handleChange}
                                        placeholder="Enter destination"
                                    />
                                </div>
                                <div className="mb-3 col-md-4">
                                    <CurrenciesWidget
                                        selectedCurrency={formData.currency}
                                        onCurrencyChange={handleCurrencyChange}
                                        label="Source Currency"
                                    />
                                    <small className="text-muted">
                                        The currency you are converting FROM
                                    </small>
                                </div>
                                <div className="mb-3 col-md-4">
                                    <label>Quotation Amount ({getCurrencySymbol(formData.currency)})</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        name="quotation_amount"
                                        value={formData.quotation_amount}
                                        onChange={handleChange}
                                        placeholder={`Enter amount in ${formData.currency}`}
                                    />
                                </div>
                            </div>

                            {/* Exchange Rate Section */}
                            <div className="card mb-3">
                                <div className="card-header bg-light">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                                        <strong>Currency Conversion</strong>
                                        <div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary me-2"
                                                onClick={handleRefreshRate}
                                                disabled={loadingRate}
                                            >
                                                {loadingRate ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                        Fetching...
                                                    </>
                                                ) : (
                                                    '⟳ Refresh Rate'
                                                )}
                                            </button>
                                            {rateLastUpdated && (
                                                <small className="text-muted">
                                                    Updated: {rateLastUpdated.toLocaleTimeString()}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="mb-3 col-md-4">
                                            <CurrenciesWidget
                                                selectedCurrency={formData.base_currency}
                                                onCurrencyChange={handleBaseCurrencyChange}
                                                label="Target Currency"
                                                includeAll={false}
                                                includeBase={true}
                                            />
                                            <small className="text-muted">
                                                The currency you are converting TO
                                            </small>
                                        </div>
                                        <div className="mb-3 col-md-4">
                                            <label>Exchange Rate</label>
                                            <div className="input-group">
                                                <span className="input-group-text">1 {formData.currency} =</span>
                                                <input
                                                    type="number"
                                                    step="0.0001"
                                                    className="form-control"
                                                    name="exchange_rate"
                                                    value={formData.exchange_rate}
                                                    onChange={handleChange}
                                                    placeholder="Auto-fetched or manual entry"
                                                />
                                                <span className="input-group-text">{formData.base_currency}</span>
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    type="button"
                                                    onClick={handleRefreshRate}
                                                    disabled={loadingRate}
                                                    title="Auto-fetch latest rate"
                                                >
                                                    ⟳
                                                </button>
                                            </div>
                                            <small className="text-muted">
                                                {loadingRate ? 'Fetching latest rate...' : 'Editable - you can modify this value'}
                                            </small>
                                        </div>
                                        <div className="mb-3 col-md-4">
                                            <label>Equivalent in {formData.base_currency}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={calculateConvertedAmount()}
                                                disabled
                                                style={{ backgroundColor: '#f8f9fa' }}
                                            />
                                            <small className="text-muted">
                                                {getConversionDescription()}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Dates */}
                            <div className="row">
                                <div className="mb-3 col-md-4">
                                    <label>Booking Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="booking_date"
                                        value={formData.booking_date}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="mb-3 col-md-4">
                                    <label>From</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="date_from"
                                        value={formData.date_from}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="mb-3 col-md-4">
                                    <label>To</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="date_to"
                                        value={formData.date_to}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            {/* Conversion Status */}
                            <div className="mb-4">
                                <label>Conversion Status</label>
                                <div className="row">
                                    {["Yes", "No", "Pending"].map((status, i) => (
                                        <div className="col-md-4" key={i}>
                                            <div className="form-check">
                                                <input
                                                    type="radio"
                                                    name="conversion_status"
                                                    value={status}
                                                    className="form-check-input"
                                                    onChange={handleChange}
                                                    checked={formData.conversion_status === status}
                                                />
                                                <label className="form-check-label">{status}</label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="text-center">
                                <button className="btn btn-custom px-5" type="submit">Save Record</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}